import { apiService, type PagingApiAndNotifierConfig, type Paging } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import { ToastOptionsSuccess, ToastOptionsError } from "@modernary/mui/components/toasts";
import { apiHosts } from "@/api/constants/apiHosts";
import type { PermitVersion, PermitVersionNew } from "./permit";
import type { AxiosResponse } from "axios";
import { toVersionInput } from "./permitApi";

const permitVersionsUrl = "permit-versions";
const versionsUrl = (permitId: string | number) => `permits/${permitId}/versions`;
const ENTITY_NAME = "Permit Version";

const axiosParams = {
  baseURL: import.meta.env.VITE_UST_API_BASE_URL,
};
const api = apiService<PermitVersion>(apiHosts.DWS, axiosParams);

const getPermitVersions = (params?: any, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  const pId = config?.permitId ?? params.permitId;
  const newUrl = pId ? versionsUrl(pId) : permitVersionsUrl;

  return api.getPaged(newUrl, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successToastCb,
    errorToastCb,
  });
};

const getPermitVersionById = (
  permitId: string | number,
  id: string | number,
  config: PagingApiAndNotifierConfig = {},
) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  const newUrl = versionsUrl(permitId);

  return api.get(newUrl + "/" + id, {
    successToastCb,
    errorToastCb,
  });
};

const getNewPermitVersion = async (permitId: string | number) => {
  const data = { permitId: Number(permitId) } as PermitVersionNew;
  return { data } as AxiosResponse<PermitVersionNew, any>;
};

const createPermitVersion2 = (
  permitId: string | number,
  version: PermitVersionNew,
  config: PagingApiAndNotifierConfig = {},
) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.created = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  const toVerIn = toVersionInput(version);

  const newUrl = versionsUrl(permitId);

  return api.post(newUrl, toVerIn, { successToastCb, errorToastCb });
};

const updatePermitVersion = (
  permitId: string | number,
  version: PermitVersion,
  config: PagingApiAndNotifierConfig = {},
) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  const toVerIn = toVersionInput(version);

  const newUrl = versionsUrl(permitId);

  return api.put(newUrl + "/" + version.id, toVerIn, {
    successToastCb,
    errorToastCb,
  });
};

const savePermitVersion = (
  permitId: string | number,
  version: PermitVersionNew | PermitVersion,
  config: PagingApiAndNotifierConfig = {},
) => {
  if (version.id) {
    return updatePermitVersion(permitId, version as PermitVersion, config);
  } else {
    return createPermitVersion2(permitId, version, config);
  }
};

const deletePermitVersionById = (
  permitId: string | number,
  id: string | number,
  config: PagingApiAndNotifierConfig = {},
) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  const newUrl = versionsUrl(permitId);

  return api.delete(newUrl + "/" + id, {
    successToastCb,
    errorToastCb,
  });
};

export {
  getPermitVersions,
  getPermitVersionById,
  deletePermitVersionById,
  savePermitVersion,
  updatePermitVersion,
  getNewPermitVersion,
};
